/* Each reporting period once.
 *
 * Quarterly figures are pieced together from two sources, and the two do not
 * agree on when a quarter ended: the filing carries the fiscal day it closed
 * (28 May), the other source the end of that month (31 May). Joined on the
 * exact date, one quarter became two. Every chart then drew it twice, the bars
 * were keyed on a label the pair shared — so switching views left old columns
 * behind and they piled up — and Rule of 40 summed a year that held the same
 * quarter twice.
 *
 * Entries whose ends fall within a few days of each other are one period. The
 * earlier date is kept, since that is the day the filing gives, and each figure
 * is taken from whichever entry has it. Real consecutive quarters are three
 * months apart and never come near the window. */

import type { Financials, StatementPeriod } from '../types';

/** How close two ends must be to be the same quarter. */
export const QUARTER_WINDOW_DAYS = 20;
/** And the same fiscal year. */
export const YEAR_WINDOW_DAYS = 60;

const DAY_MS = 86_400_000;

function daysApart(a: string, b: string): number {
  return Math.abs(Date.parse(`${b.slice(0, 10)}T00:00:00Z`) - Date.parse(`${a.slice(0, 10)}T00:00:00Z`)) / DAY_MS;
}

const either = (first: number | null, second: number | null): number | null => first ?? second;

export function mergeSamePeriods(periods: StatementPeriod[], withinDays: number): StatementPeriod[] {
  const ordered = periods
    .filter((p) => p && typeof p.end === 'string' && p.end !== '')
    .sort((a, b) => a.end.localeCompare(b.end));
  const out: StatementPeriod[] = [];
  for (const p of ordered) {
    const last = out[out.length - 1];
    if (last && daysApart(last.end, p.end) <= withinDays) {
      out[out.length - 1] = {
        end: last.end,
        revenue: either(last.revenue, p.revenue),
        netIncome: either(last.netIncome, p.netIncome),
        grossProfit: either(last.grossProfit, p.grossProfit),
        operatingIncome: either(last.operatingIncome, p.operatingIncome),
      };
    } else {
      out.push({ ...p });
    }
  }
  return out;
}

/** The statements with every period once, quarterly and annual. */
export function normaliseFinancials(f: Financials): Financials {
  return {
    ...f,
    quarterly: mergeSamePeriods(f.quarterly, QUARTER_WINDOW_DAYS),
    annual: mergeSamePeriods(f.annual, YEAR_WINDOW_DAYS),
  };
}
